import React from 'react'

import { ActivityFeed } from 'features/Activity/ActivityFeed'
import { LayoutEditorControlSeparator } from 'features/views/LayoutEditor/controls/LayoutEditorControlSeparator'
import { LayoutEditorVisibilityFiltersControl } from 'features/views/LayoutEditor/controls/LayoutEditorVisibilityFiltersControl'
import {
    Widget,
    WidgetAdminControlsComponent,
    WidgetComponent,
} from 'features/views/LayoutEditor/types'

import { Box } from 'ui/components/Box'
import { Field, InfoMark } from 'ui/components/Field'
import { RadioCard, RadioCardGroup } from 'ui/components/Radio'
import { theme } from 'ui/styling/Theme.css'

import { useFeedWidgetState } from './hooks/useFeedWidgetState'

import { FeedWidgetActivityFeedWrapperStyles } from './FeedWidget.css'

export type FeedWidgetType = Widget<{
    size?: 'auto' | 'small' | 'medium' | 'large'
}>

type FeedWidgetProps = {}

const FeedWidgetComponent: WidgetComponent<FeedWidgetType, FeedWidgetProps> = ({ widget }) => {
    const { size = 'small' } = widget.attrs

    const { objectSid, recordSid, feedTarget, feedParams, showFeed } = useFeedWidgetState()

    if (!showFeed) return null

    return (
        <Box py="m">
            <ActivityFeed
                key={recordSid}
                contextKey={objectSid!}
                target={feedTarget}
                params={feedParams}
                showLocationPreview={true}
                feedWrapperProps={{
                    className: FeedWidgetActivityFeedWrapperStyles.styleFunction({ size }),
                }}
            />
        </Box>
    )
}

export const FeedWidget = React.memo(FeedWidgetComponent)

type FeedWidgetAdminControlsProps = {}

export const FeedWidgetAdminControls: WidgetAdminControlsComponent<
    FeedWidgetType,
    FeedWidgetAdminControlsProps
> = ({ widget, onChange }) => {
    const { size = 'small' } = widget.attrs

    return (
        <Box flex column gap="3xl" height="full" overflowY="auto">
            <Box flex column gap="3xl" px="l">
                <Field label="Size">
                    <RadioCardGroup
                        value={size}
                        onValueChange={(value: string) => {
                            const newValue = value || undefined

                            onChange((attrs) => {
                                attrs.set('size', newValue)
                            })
                        }}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, 1fr)',
                        }}
                    >
                        <RadioCard
                            value="auto"
                            icon={{ name: 'Expand' }}
                            cardIconWrapperProps={{
                                style: {
                                    height: '45px',
                                    padding: `${theme.space.m} 0`,
                                },
                            }}
                        >
                            <Box flex center justifyContent="center">
                                Auto
                                <InfoMark size="xs" zIndex={1500}>
                                    Automatically adjust height based on content
                                </InfoMark>
                            </Box>
                        </RadioCard>
                        <RadioCard
                            value="small"
                            icon={() => <FeedWidgetSizeSmallIcon isActive={size === 'small'} />}
                            cardIconWrapperProps={{
                                style: {
                                    padding: `${theme.space.m} 0`,
                                },
                            }}
                        >
                            Small
                        </RadioCard>
                        <RadioCard
                            value="medium"
                            icon={() => <FeedWidgetSizeMediumIcon isActive={size === 'medium'} />}
                            cardIconWrapperProps={{
                                style: {
                                    padding: `${theme.space.m} 0`,
                                },
                            }}
                        >
                            Medium
                        </RadioCard>
                        <RadioCard
                            value="large"
                            icon={() => <FeedWidgetSizeLargeIcon isActive={size === 'large'} />}
                            cardIconWrapperProps={{
                                style: {
                                    padding: `${theme.space.m} 0`,
                                },
                            }}
                        >
                            Large
                        </RadioCard>
                    </RadioCardGroup>
                </Field>
            </Box>
            <LayoutEditorControlSeparator />
            <LayoutEditorVisibilityFiltersControl widget={widget} onChange={onChange} />
        </Box>
    )
}

type RadioCardIconProps = {
    isActive: boolean
}

const FeedWidgetSizeSmallIcon: React.FC<RadioCardIconProps> = ({ isActive }) => {
    return (
        <svg
            width="40px"
            height="100%"
            viewBox="0 0 40 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="0.5"
                y="10"
                width="39"
                height="8"
                rx="1.5"
                style={{
                    transition: theme.transition.colors,
                    stroke: isActive ? theme.color.theme600 : theme.color.gray300,
                }}
            />
            <rect
                y="9.5"
                width="34"
                height="3"
                rx="1"
                transform="matrix(-1 0 0 1 37 3)"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
        </svg>
    )
}

const FeedWidgetSizeMediumIcon: React.FC<RadioCardIconProps> = ({ isActive }) => {
    return (
        <svg
            width="40px"
            height="100%"
            viewBox="0 0 40 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="0.5"
                y="6"
                width="39"
                height="15"
                rx="1.5"
                style={{
                    transition: theme.transition.colors,
                    stroke: isActive ? theme.color.theme600 : theme.color.gray300,
                }}
            />
            <rect
                y="5.5"
                width="34"
                height="10"
                rx="1"
                transform="matrix(-1 0 0 1 37 3)"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
        </svg>
    )
}

const FeedWidgetSizeLargeIcon: React.FC<RadioCardIconProps> = ({ isActive }) => {
    return (
        <svg
            width="40px"
            height="100%"
            viewBox="0 0 40 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="0.5"
                y="1.5"
                width="39"
                height="24"
                rx="1.5"
                style={{
                    transition: theme.transition.colors,
                    stroke: isActive ? theme.color.theme600 : theme.color.gray300,
                }}
            />
            <rect
                y="1"
                width="34"
                height="19"
                rx="1"
                transform="matrix(-1 0 0 1 37 3)"
                style={{
                    transition: theme.transition.colors,
                    fill: isActive ? theme.color.theme100 : theme.color.gray100,
                }}
            />
        </svg>
    )
}
